import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
    getProjectTrackerStaff,
    getProjectTrackerProjects,
    getMyProjects,
    getProjectTrackerTasks,
    createProjectTrackerProject,
    getProjectTrackerProjectById,
    updateProjectTrackerProject,
    completeProjectTrackerProject,
    getMyTasks,
    getTask,
    createTask,
    updateTask,
    deleteTask,
    submitTask,
    withdrawTaskSubmission,
    getMyReviews,
    getToReviews,
    getCompletedToReviews,
    submitReview,
    getMyProjectsOnly,
    getMyDeliverables,
    getMyTeamMemberProjects,
    scheduleTask,
    updateTaskWorkingPeriod,
    getLeaderboardScores,
    getLeaderboardPerformanceCard,
} from 'services/api/task-management'

import { myAccount } from '../authentication'
import {
    protectedQueryRefetchInterval,
    retryProtectedQuery,
} from 'services/session/queryPolicy'

export const PROJECT_TRACKER_STAFF_QK = 'project-tracker-staff'
export const PROJECT_TRACKER_PROJECTS_QK = 'project-tracker-projects'
export const MY_PROJECTS_QK = 'my-projects'
export const MY_ACCOUNT_QK = 'my-account'
export const PROJECT_TRACKER_TASKS_QK = 'project-tracker-tasks'
export const ALL_TASKS_QK = PROJECT_TRACKER_TASKS_QK
export const MY_TASKS_QK = 'my-tasks'
export const TASK_DETAIL_QK = 'task-detail'
export const MY_REVIEWS_QK = 'my-reviews'
export const TO_REVIEWS_QK = 'to-reviews'
export const COMPLETED_TO_REVIEWS_QK = 'completed-to-reviews'
export const MY_PROJECTS_ONLY_QK = 'my-projects-only'
export const MY_DELIVERABLES_QK = 'my-deliverables'
export const MY_TEAM_MEMBER_PROJECTS_QK = 'my-team-member-projects'
export const LEADERBOARD_SCORES_QK = 'leaderboard-scores'
export const LEADERBOARD_PERFORMANCE_QK = 'leaderboard-performance'
export const PROJECT_TRACKER_PROJECT_QK = 'project-tracker-project'

const DEFAULT_QUERY_OPTIONS = {
    staleTime: 300000,
    cacheTime: 600000,
    refetchOnWindowFocus: false,
    retry: retryProtectedQuery,
    retryDelay: 1000,
    refetchInterval: protectedQueryRefetchInterval,
}

export const useGetProjectTrackerStaff = (params = {}, options = {}) => {
    return useQuery([PROJECT_TRACKER_STAFF_QK, params], () => getProjectTrackerStaff(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useGetProjectTrackerProjects = (params = {}, options = {}) => {
    return useQuery([PROJECT_TRACKER_PROJECTS_QK, params], () => getProjectTrackerProjects(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useGetMyProjects = (params = {}, options = {}) => {
    return useQuery([MY_PROJECTS_QK, params], () => getMyProjects(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useMyAccount = (options = {}) => {
    return useQuery([MY_ACCOUNT_QK], myAccount, {
        staleTime: 300000,
        cacheTime: 600000,
        refetchOnWindowFocus: false,
        retry: retryProtectedQuery,
        ...options,
    })
}

/**
 * ----------------------------
 * Tasks (GET /tasks)
 * ----------------------------
 * We keep BOTH hooks for compatibility:
 * - useGetAllTasks (older code)
 * - useGetProjectTrackerTasks (newer naming)
 * Both call the same API function.
 */
export const useGetProjectTrackerTasks = (params = {}, options = {}) => {
    return useQuery([PROJECT_TRACKER_TASKS_QK, params], () => getProjectTrackerTasks(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useGetAllTasks = (params = {}, options = {}) => {
    return useQuery([ALL_TASKS_QK, params], () => getProjectTrackerTasks(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useGetMyTasks = (params = {}, options = {}) => {
    return useQuery([MY_TASKS_QK, params], () => getMyTasks(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useGetTask = (taskId, options = {}) => {
    return useQuery([TASK_DETAIL_QK, taskId], () => getTask(taskId), {
        ...DEFAULT_QUERY_OPTIONS,
        enabled: !!taskId,
        ...options,
    })
}

export const useGetMyDeliverables = (projectId, options = {}) => {
    return useQuery([MY_DELIVERABLES_QK, projectId], () => getMyDeliverables(projectId), {
        ...DEFAULT_QUERY_OPTIONS,
        enabled: !!projectId,
        ...options,
    })
}

export const useGetMyTeamMemberProjects = (params = {}, options = {}) => {
    return useQuery([MY_TEAM_MEMBER_PROJECTS_QK, params], () => getMyTeamMemberProjects(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useCreateTask = (options = {}) => {
    const qc = useQueryClient()

    return useMutation((taskData) => createTask(taskData), {
        onSuccess: (data) => {
            qc.invalidateQueries([ALL_TASKS_QK])
            qc.invalidateQueries([MY_TASKS_QK])
            qc.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
            options?.onSuccess?.(data)
        },
        onError: (error) => {
            options?.onError?.(error)
        },
        ...options,
    })
}

export const useUpdateTask = (options = {}) => {
    const qc = useQueryClient()

    return useMutation(({ taskId, taskData }) => updateTask(taskId, taskData), {
        onSuccess: (data, variables) => {
            qc.invalidateQueries([ALL_TASKS_QK])
            qc.invalidateQueries([MY_TASKS_QK])
            qc.invalidateQueries([TASK_DETAIL_QK, variables.taskId])
            qc.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
            options?.onSuccess?.(data, variables)
        },
        onError: (error) => {
            options?.onError?.(error)
        },
        ...options,
    })
}

export const useDeleteTask = (options = {}) => {
    const qc = useQueryClient()

    return useMutation((taskId) => deleteTask(taskId), {
        onSuccess: (data, taskId) => {
            qc.invalidateQueries([ALL_TASKS_QK])
            qc.invalidateQueries([MY_TASKS_QK])
            qc.invalidateQueries([TASK_DETAIL_QK, taskId])
            qc.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
            options?.onSuccess?.(data, taskId)
        },
        onError: (error) => {
            options?.onError?.(error)
        },
        ...options,
    })
}

export const useSubmitTask = (options = {}) => {
    const qc = useQueryClient()

    return useMutation((submitData) => submitTask(submitData), {
        onSuccess: (data, variables) => {
            qc.invalidateQueries([ALL_TASKS_QK])
            qc.invalidateQueries([MY_TASKS_QK])
            qc.invalidateQueries([TASK_DETAIL_QK, variables?.task_id])
            qc.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
            qc.invalidateQueries([MY_REVIEWS_QK])
            qc.invalidateQueries([TO_REVIEWS_QK])
            qc.invalidateQueries([COMPLETED_TO_REVIEWS_QK])
            options?.onSuccess?.(data, variables)
        },
        onError: (error) => {
            options?.onError?.(error)
        },
        ...options,
    })
}

export const useWithdrawTaskSubmission = (options = {}) => {
    const qc = useQueryClient()

    return useMutation((taskId) => withdrawTaskSubmission(taskId), {
        onSuccess: (data, taskId) => {
            qc.invalidateQueries([ALL_TASKS_QK])
            qc.invalidateQueries([MY_TASKS_QK])
            qc.invalidateQueries([TASK_DETAIL_QK, taskId])
            qc.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
            qc.invalidateQueries([MY_REVIEWS_QK])
            qc.invalidateQueries([TO_REVIEWS_QK])
            qc.invalidateQueries([COMPLETED_TO_REVIEWS_QK])
            qc.invalidateQueries([MY_TEAM_MEMBER_PROJECTS_QK])
            qc.invalidateQueries('notifications')
            options?.onSuccess?.(data, taskId)
        },
        onError: (error, taskId) => {
            options?.onError?.(error, taskId)
        },
        ...options,
    })
}

export const useSubmitReview = (options = {}) => {
    const qc = useQueryClient()

    return useMutation(({ taskId, reviewData }) => submitReview(taskId, reviewData), {
        onSuccess: (data, variables) => {
            qc.invalidateQueries([ALL_TASKS_QK])
            qc.invalidateQueries([MY_TASKS_QK])
            qc.invalidateQueries([MY_REVIEWS_QK])
            qc.invalidateQueries([TO_REVIEWS_QK])
            qc.invalidateQueries([COMPLETED_TO_REVIEWS_QK])
            qc.invalidateQueries([TASK_DETAIL_QK, variables?.taskId])
            qc.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
            qc.invalidateQueries([MY_TEAM_MEMBER_PROJECTS_QK])
            qc.refetchQueries([MY_REVIEWS_QK])
            qc.refetchQueries([TO_REVIEWS_QK])
            qc.refetchQueries([COMPLETED_TO_REVIEWS_QK])

            options?.onSuccess?.(data, variables)
        },
        onError: (error) => {
            options?.onError?.(error)
        },
        ...options,
    })
}

export const useGetMyReviews = (params = {}, options = {}) => {
    return useQuery([MY_REVIEWS_QK, params], () => getMyReviews(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useGetToReviews = (params = {}, options = {}) => {
    return useQuery([TO_REVIEWS_QK, params], () => getToReviews(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useGetCompletedToReviews = (params = {}, options = {}) => {
    return useQuery([COMPLETED_TO_REVIEWS_QK, params], () => getCompletedToReviews(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useGetMyProjectsOnly = (params = {}, options = {}) => {
    return useQuery([MY_PROJECTS_ONLY_QK, params], () => getMyProjectsOnly(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useScheduleTask = (options = {}) => {
    const qc = useQueryClient()

    return useMutation(({ taskId, scheduleData }) => scheduleTask(taskId, scheduleData), {
        onSuccess: (data, variables) => {
            qc.invalidateQueries([ALL_TASKS_QK])
            qc.invalidateQueries([MY_TASKS_QK])
            qc.invalidateQueries([TASK_DETAIL_QK, variables?.taskId])
            qc.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
            options?.onSuccess?.(data, variables)
        },
        onError: (error) => {
            options?.onError?.(error)
        },
        ...options,
    })
}

export const useUpdateTaskWorkingPeriod = (options = {}) => {
    const qc = useQueryClient()

    return useMutation(({ taskId, workingPeriodData }) => updateTaskWorkingPeriod(taskId, workingPeriodData), {
        onSuccess: (data, variables) => {
            qc.invalidateQueries([ALL_TASKS_QK])
            qc.invalidateQueries([MY_TASKS_QK])
            qc.invalidateQueries([TASK_DETAIL_QK, variables?.taskId])
            qc.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
            options?.onSuccess?.(data, variables)
        },
        onError: (error) => {
            options?.onError?.(error)
        },
        ...options,
    })
}

export const useGetLeaderboardScores = (params = {}, options = {}) => {
    return useQuery([LEADERBOARD_SCORES_QK, params], () => getLeaderboardScores(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useGetLeaderboardPerformanceCard = (params = {}, options = {}) => {
    return useQuery([LEADERBOARD_PERFORMANCE_QK, params], () => getLeaderboardPerformanceCard(params), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useCreateProjectTrackerProject = (options = {}) => {
    const qc = useQueryClient()

    return useMutation((payload) => createProjectTrackerProject(payload), {
        onSuccess: (data) => {
            qc.invalidateQueries(PROJECT_TRACKER_PROJECTS_QK)

            // important: refresh MyTasks “team member projects” list too
            qc.invalidateQueries(MY_TEAM_MEMBER_PROJECTS_QK)

            // (optional) if your “My Tasks” page also depends on tasks list
            qc.invalidateQueries(MY_TASKS_QK)

            options?.onSuccess?.(data)
        },
        onError: (error) => {
            options?.onError?.(error)
        },
        ...options,
    })
}

export const useGetProjectTrackerProjectById = (projectId, options = {}) => {
    return useQuery([PROJECT_TRACKER_PROJECT_QK, projectId], () => getProjectTrackerProjectById(projectId), {
        enabled: Boolean(projectId) && (options.enabled ?? true),
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    })
}

export const useUpdateProjectTrackerProject = (options = {}) => {
    const qc = useQueryClient()

    return useMutation(({ projectId, payload }) => updateProjectTrackerProject(projectId, payload), {
        onSuccess: (_, vars) => {
            qc.invalidateQueries(PROJECT_TRACKER_PROJECTS_QK)
            qc.invalidateQueries([PROJECT_TRACKER_PROJECT_QK, vars?.projectId])
            options?.onSuccess?.(_, vars)
        },
        onError: (error) => {
            options?.onError?.(error)
        },
        ...options,
    })
}

export const useCompleteProjectTrackerProject = (options = {}) => {
    const qc = useQueryClient()

    return useMutation((projectId) => completeProjectTrackerProject(projectId), {
        onSuccess: (res, projectId) => {
            qc.invalidateQueries(PROJECT_TRACKER_PROJECTS_QK)
            qc.invalidateQueries([PROJECT_TRACKER_PROJECT_QK, projectId])

            const updatedProject = res?.data?.project
            if (updatedProject?.id) {
                qc.setQueryData([PROJECT_TRACKER_PROJECT_QK, updatedProject.id], (old) => {
                    if (!old) return res
                    return {
                        ...old,
                        data: {
                            ...(old?.data ?? {}),
                            project: {
                                ...(old?.data?.project ?? {}),
                                ...updatedProject,
                            },
                        },
                    }
                })
            }

            options?.onSuccess?.(res, projectId)
        },
        onError: (error) => {
            options?.onError?.(error)
        },
        ...options,
    })
}
